import type { IntakeField } from './flow.js'

/**
 * Whether one typed value is acceptable, with NO runtime dependencies.
 *
 * Deliberately its own module, reachable as `@fitting-room/contracts/intake`.
 * The customer client imports nothing but TYPES from this package — it
 * validates nothing at runtime and carries no schema library — and this is the
 * one rule it does need at runtime. Importing it through the barrel would pull
 * every zod schema in the package into a bundle a bride loads on her phone;
 * measured, that was 75KB.
 *
 * The rule lives in the contract rather than in either side because both need
 * the same answer: the domain turns a rejection into a `DomainError`, and the
 * client turns it into a localised message under the input. A client gating on
 * a different rule than the server enforces is a form that refuses the value it
 * just accepted, with no way to learn why.
 */
/** What a text field accepts when the flow does not say. */
export const DEFAULT_INTAKE_MAX_LENGTH = 500

/**
 * A wedding is a day, not an instant: storing it with a time would make it
 * move across a time zone.
 */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Whether a string is a real calendar day.
 *
 * Exported because a day is asked for in one place that is not an intake field
 * — the booking a guest says she already has — and both the domain and the
 * client have to refuse the same strings there as they do here. One rule, one
 * place, whoever is asking.
 */
export function isCalendarDay(text: string): boolean {
  if (!CALENDAR_DAY.test(text)) return false
  // `Date.parse` rolls 2027-02-30 forward to March rather than refusing it,
  // so the only reliable check is that the day survives a round trip.
  const parsed = new Date(`${text}T00:00:00.000Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === text
}
/**
 * Deliberately permissive. This has to accept the address she actually uses,
 * and every stricter pattern is a well-known way to reject a valid one.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type IntakeRejection =
  | { reason: 'blank' }
  | { reason: 'not_a_number' }
  | { reason: 'below_min'; min: number }
  | { reason: 'above_max'; max: number }
  | { reason: 'not_a_date' }
  | { reason: 'not_an_email' }
  | { reason: 'too_long'; maxLength: number }

export type IntakeCheck = { ok: true; value: string | number } | ({ ok: false } & IntakeRejection)

/**
 * Check one typed value against the field that asked for it.
 *
 * Here, in the contract, because both sides need the same answer: the domain
 * turns a rejection into a `DomainError` and the client turns it into a
 * localised message under the input. One rule set — a client that gates on a
 * different rule than the server enforces is a form that refuses the value it
 * just accepted, with no way to learn why.
 *
 * There is no "we will guess" branch. A silently coerced measurement turns up
 * on a pull sheet as a number nobody can account for.
 */
export function checkIntakeValue(field: IntakeField, raw: string | number): IntakeCheck {
  const text = typeof raw === 'number' ? String(raw) : raw.trim()
  if (text === '') return { ok: false, reason: 'blank' }

  switch (field.kind) {
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(text)
      if (!Number.isFinite(n)) return { ok: false, reason: 'not_a_number' }
      if (field.min !== undefined && n < field.min) return { ok: false, reason: 'below_min', min: field.min }
      if (field.max !== undefined && n > field.max) return { ok: false, reason: 'above_max', max: field.max }
      return { ok: true, value: n }
    }
    case 'date': {
      if (!isCalendarDay(text)) return { ok: false, reason: 'not_a_date' }
      return { ok: true, value: text }
    }
    case 'email': {
      if (!EMAIL.test(text)) return { ok: false, reason: 'not_an_email' }
      return { ok: true, value: text }
    }
    default: {
      const maxLength = field.maxLength ?? DEFAULT_INTAKE_MAX_LENGTH
      if (text.length > maxLength) return { ok: false, reason: 'too_long', maxLength }
      return { ok: true, value: text }
    }
  }
}

